import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { ApiError, get, post, put } from "../api/client";
import { useLoad } from "../api/hooks";
import type { LiveOverlay, LiveOverlays } from "../api/live";
import type { Clip, ClipAssignees, ClipList, Metric, Roster, UserSearchResult, Video } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { cleanHandleInput, initialsOf } from "../components/HandleSheet";
import { LiveClipPlayer, useRenderOptions } from "../components/LiveClipPlayer";
import { Shell } from "../components/Shell";
import { ErrorLine, Sheet, useToast } from "../components/ui";
import { plural } from "../lib/format";
import { prefetchClipUrls } from "../lib/clipPrefetch";
import { numbersOf, placeLine, hitLine } from "../lib/clips";
import { deliverySummary } from "../lib/descriptions";
import { resolveRoster } from "../lib/roster";
import { downloadName, exportLabel, useExport } from "../lib/exports";
import { useIsDesktop } from "../lib/device";
import { MatchTimeline } from "../components/MatchTimeline";
import { ViewingOptions } from "../components/ViewingOptions";
import { MatchPlaybackControls } from "../components/MatchPlaybackControls";
import { clipAnchor, clipMediaOffset, matchSource } from "../lib/timeline";
import { enterFull, exitFull, fullElement, onFullChange } from "../lib/fullscreen";

/** Clip playback and a full working-copy timeline share the same source clock. */
export default function ClipsPage({ betaReview = false }: { betaReview?: boolean }) {
  const { id = "" } = useParams();
  const matchPath = betaReview ? `/admin/beta-submissions/${id}` : `/videos/${id}`;
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const desktop = useIsDesktop();
  const { me, plan, refresh } = useAuth();
  const showFlagged = params.get("flagged") === "1";
  const showUnassigned = params.get("unassigned") === "1";
  const video = useLoad(() => get<{ video: Video & { uploader_email?: string } }>(betaReview ? `/ops/beta/submissions/${id}` : `/v1/videos/${id}`).then((r) => r.video), [id, betaReview]);
  // clip times are on the working copy; a part of the upload starts later in it
  const offset = video.data?.segment?.start_ms ?? 0;
  const list = useLoad(() => get<ClipList>(`/v1/videos/${id}/deliveries`), [id]);
  const rosterApi = useLoad(() => get<Roster>(`/v1/videos/${id}/players`), [id]);
  const overlays = useLoad(() => get<LiveOverlays>(`/v1/videos/${id}/overlays`), [id]);
  const [render, setRender] = useRenderOptions();
  const all = list.data?.items ?? [];
  const roster = resolveRoster(rosterApi.data);
  // a link from before a merge names the merged player: show the player it joined
  const batsman = roster.shown(params.get("batsman"));
  const bowler = roster.shown(params.get("bowler"));
  const clips = useMemo(() => all.filter((c) => {
    if (showFlagged) return c.flagged;
    if (c.flagged) return false;
    if (showUnassigned) {
      const hasMarkedUnassigned = all.some((x) => !x.flagged && (x.assignees?.batsman.unassigned || x.assignees?.bowler.unassigned));
      if (hasMarkedUnassigned) return !!(c.assignees?.batsman.unassigned || c.assignees?.bowler.unassigned);
      return !!(c.assignees?.batsman.unassigned || c.assignees?.bowler.unassigned || !c.assignees?.batsman.assignee || !c.assignees?.bowler.assignee);
    }
    const a = roster.assign(c);
    return (!batsman || a.batsman_id === batsman) && (!bowler || a.bowler_id === bowler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [all, batsman, bowler, showFlagged, showUnassigned, rosterApi.data]);
  const [i, setI] = useState(() => Math.max(0, Number(params.get("i") ?? "0") || 0));
  useEffect(() => {
    if (!list.data || ((params.get("batsman") || params.get("bowler")) && !rosterApi.data)) return;
    if (i >= clips.length) setI(Math.max(0, clips.length - 1));
  }, [clips.length, i, list.data, rosterApi.data, params]);
  const cur = clips[i] ?? null;
  const videoEl = useRef<HTMLVideoElement | null>(null);
  const source = matchSource(all, id);
  const [mediaDuration, setMediaDuration] = useState(0);
  const duration = mediaDuration || video.data?.duration_ms || 0;
  const [time, setTime] = useState(0);
  const [mediaState, setMediaState] = useState("loading");
  const [playback, setPlayback] = useState<{ src: string; gap: boolean; seek: { seconds: number; playing: boolean; serial: number } } | null>(null);
  const inspected = useRef<{ playing: boolean; ms: number } | null>(null);
  const resumePlayback = useRef(true);
  const serial = useRef(0);
  const stage = useRef<HTMLDivElement>(null);
  const [full, setFull] = useState(false);
  const selectClip = (k: number) => { setPlayback(null); setI(k); };
  const startInspection = () => {
    const v = videoEl.current;
    inspected.current = { playing: !!v && !v.paused, ms: (v?.currentTime ?? time / 1000) * 1000 };
    v?.pause();
  };
  const cancelInspection = () => {
    if (inspected.current?.playing) void videoEl.current?.play().catch(() => {});
    inspected.current = null;
  };
  const seekMatch = (ms: number, near: number | null) => {
    const playing = inspected.current?.playing ?? (!!videoEl.current && !videoEl.current.paused);
    inspected.current = null;
    if (!source) {
      if (src) {
        setPlayback({ src, gap: false, seek: { seconds: Math.max(0, (ms - mediaOffset) / 1000), playing, serial: ++serial.current } });
      } else say("Full match video is unavailable. Choose a clip with the arrow buttons.");
      return;
    }
    const selected = near != null ? clips[near] : null;
    if (near != null) setI(near);
    setTime(ms);
    setMediaState("loading");
    setPlayback({ src: selected ? `${source}#t=${(ms / 1000).toFixed(3)},${(selected.window_ms.end / 1000).toFixed(3)}` : source, gap: !selected,
      seek: { seconds: ms / 1000, playing, serial: ++serial.current } });
  };
  const src = playback?.src ?? cur?.clip.playback_url ?? null;
  const mediaOffset = clipMediaOffset(cur, src);
  useEffect(() => { setPlayback(null); }, [id, batsman, bowler, showFlagged, showUnassigned]);
  useEffect(() => { setMediaDuration(0); setTime(0); }, [id]);
  useEffect(() => {
    const v = videoEl.current;
    if (!v) return;
    const update = () => { if (!inspected.current) setTime(v.currentTime * 1000 + mediaOffset); };
    const metadata = () => { if (source && Number.isFinite(v.duration)) setMediaDuration(v.duration * 1000); };
    const ready = () => { setMediaState("ready"); update(); };
    const loading = () => setMediaState("loading");
    const error = () => setMediaState("error");
    const play = () => { resumePlayback.current = true; };
    const pause = () => { if (!inspected.current && !v.error && !v.seeking && v.readyState >= 2) resumePlayback.current = false; };
    v.addEventListener("timeupdate", update); v.addEventListener("loadedmetadata", metadata);
    v.addEventListener("seeked", ready); v.addEventListener("canplay", ready); v.addEventListener("playing", ready);
    v.addEventListener("waiting", loading); v.addEventListener("seeking", loading); v.addEventListener("error", error);
    v.addEventListener("playing", play); v.addEventListener("pause", pause);
    metadata();
    if (v.readyState >= 2) ready(); else setMediaState("loading");
    return () => {
      v.removeEventListener("timeupdate", update); v.removeEventListener("loadedmetadata", metadata);
      v.removeEventListener("seeked", ready); v.removeEventListener("canplay", ready); v.removeEventListener("playing", ready);
      v.removeEventListener("waiting", loading); v.removeEventListener("seeking", loading); v.removeEventListener("error", error);
      v.removeEventListener("playing", play); v.removeEventListener("pause", pause);
    };
  }, [src, source, mediaOffset]);
  useEffect(() => {
    const el = stage.current;
    if (!el) return;
    const off = onFullChange(() => setFull(fullElement() === el));
    return () => { off(); if (fullElement() === el) exitFull(); };
  }, [!!cur]);
  useEffect(() => {
    prefetchClipUrls([
      clips[i + 1]?.clip.playback_url,
      clips[i + 2]?.clip.playback_url,
      clips[i - 1]?.clip.playback_url,
    ]);
  }, [clips, i]);
  const [toast, say] = useToast();
  const [confirmFlag, setConfirmFlag] = useState<Clip | null>(null);
  const exp = useExport();
  const expAll = useExport();
  useEffect(() => { exp.reset(); }, [cur?.id]);
  useEffect(() => { expAll.reset(); }, [id, batsman, bowler, showFlagged, showUnassigned, clips.length]);
  useEffect(() => {
    for (const s of [exp.state, expAll.state]) {
      if (s.phase === "ready") say("Your download is ready.");
      if (s.phase === "failed") say(s.message);
    }
  }, [exp.state, expAll.state, say]);

  const patch = useCallback((clipId: string, fn: (c: Clip) => Clip) => {
    if (!list.data) return;
    list.set({ ...list.data, items: list.data.items.map((c) => (c.id === clipId ? fn(c) : c)) });
  }, [list]);

  const flag = async (clip: Clip, flagged: boolean) => {
    setConfirmFlag(null);
    try {
      await post(`/v1/deliveries/${clip.id}/flag`, { flagged });
      setPlayback(null);
      patch(clip.id, (c) => ({ ...c, flagged }));
      say(flagged ? "Marked as not a delivery. Thanks, that helps." : "Restored.");
    } catch (e) {
      say((e as Error).message);
    }
  };
  const unlock = async (clip: Clip) => {
    try {
      const r = await post<{ metrics: Metric[]; previews_used: number }>(`/v1/deliveries/${clip.id}/review`, { action: "preview" });
      patch(clip.id, (c) => ({ ...c, metrics: r.metrics, previewed: true }));
      if (list.data?.preview) list.set({ ...list.data, items: list.data.items.map((c) => (c.id === clip.id ? { ...c, metrics: r.metrics, previewed: true } : c)), preview: { ...list.data.preview, used: r.previews_used } });
    } catch (e) {
      say(e instanceof ApiError && e.code === "ALLOWANCE_EXHAUSTED" ? "All 5 previews used on this video. Every number is on Basic." : (e as Error).message);
    }
  };

  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || clips.length === 0) return;
      if (document.querySelector(".sheet")) return;
      if (e.target instanceof Element && e.target.closest('input, textarea, select, button, a, video, [contenteditable="true"]')) return;
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        e.preventDefault();
        setPlayback(null);
        setI((x) => Math.max(0, Math.min(clips.length - 1, x + (e.key === "ArrowRight" ? 1 : -1))));
      }
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [clips.length]);
  useEffect(() => {
    const p = new URLSearchParams(params);
    p.set("i", String(i));
    setParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);
  const [auto, setAuto] = useState(readAutoplay);
  const toggleAuto = () => {
    const on = !auto;
    setAuto(on);
    writeAutoplay(on);
  };
  const nextClip = useCallback(() => {
    if (auto && !inspected.current && !playback?.gap && i + 1 < clips.length) {
      setPlayback(null); setI(i + 1);
    }
  }, [auto, clips.length, i, playback?.gap]);

  const who = batsman && bowler ? `${roster.name(bowler)} to ${roster.name(batsman)}` : batsman ? roster.name(batsman) : bowler ? roster.name(bowler) : showFlagged ? "Not a delivery" : showUnassigned ? "Unassigned clips" : "Whole match";
  const n = numbersOf(cur ?? ({ metrics: { status: "locked" } } as unknown as Clip));
  const previewsLeft = list.data?.preview ? list.data.preview.allowed - list.data.preview.used : 0;
  const bowlerOf = (c: Clip) =>
    c.assignees?.bowler.unassigned
      ? "Unassigned"
      : c.assignees?.bowler.assignee?.handle
        ? `@${c.assignees.bowler.assignee.handle}`
        : roster.assign(c).bowler_id ? roster.name(roster.assign(c).bowler_id) : "Unknown bowler";
  const batsmanOf = (c: Clip) =>
    c.assignees?.batsman.unassigned
      ? "Unassigned"
      : c.assignees?.batsman.assignee?.handle
        ? `@${c.assignees.batsman.assignee.handle}`
        : roster.assign(c).batsman_id ? roster.name(roster.assign(c).batsman_id) : "Unknown batsman";
  // One player picked: the header shows their picture, and the stage's label beside the title if named.
  const role: "bowler" | "batsman" | null = bowler && !batsman ? "bowler" : batsman && !bowler ? "batsman" : null;
  const roleId = role === "bowler" ? bowler : batsman;
  const label = roleId && roster.name(roleId) !== roster.label(roleId) ? roster.label(roleId) : null;

  const frame = overlays.data?.frame ?? {
    width: video.data?.width ?? 1280,
    height: video.data?.height ?? 720,
    fps: video.data?.fps ?? 29.97,
  };
  const overlay: LiveOverlay | null = useMemo(() => {
    if (!cur) return null;
    const ovMap = overlays.data?.deliveries;
    const raw = ovMap ? ovMap[cur.candidate_id] ?? ovMap[`${id}_${cur.candidate_id}`] ?? null : null;
    if (n.locked) {
      return raw ? { ...raw, speeds: { bowl_kmh: null, bat_kmh: null, impact_kmh: null } } : null;
    }
    const relFrame = cur.events_ms.release != null ? Math.round((cur.events_ms.release / 1000) * frame.fps) : null;
    const batFrame = cur.events_ms.impact != null ? Math.round((cur.events_ms.impact / 1000) * frame.fps) : null;
    if (raw) {
      const bowl = raw.speeds.bowl_kmh ?? n.speed ?? null;
      const bat = raw.speeds.bat_kmh ?? n.batSpeed ?? null;
      const impact = raw.speeds.impact_kmh ?? n.impactSpeed ?? null;
      const rel = raw.frames.release ?? raw.frames.release_drawn ?? relFrame;
      return {
        ...raw,
        frames: { ...raw.frames, release: rel },
        speeds: { ...raw.speeds, bowl_kmh: bowl, bat_kmh: bat, impact_kmh: impact },
      };
    }
    if (n.speed == null && n.batSpeed == null) return null;
    return {
      delivery_id: cur.candidate_id,
      frames: { release: relFrame, bounce: null, bat: batFrame },
      impact_type: cur.impact_type,
      contact: cur.impact_type === "bat",
      events_from: "m09",
      rejected: false,
      speeds: { bowl_kmh: n.speed ?? null, bat_kmh: n.batSpeed ?? null, impact_kmh: n.impactSpeed ?? null },
      zone: null,
      path: [],
      hit: [],
      bounce_px: null,
      bat_px: null,
      end: null,
      counts: {},
    };
  }, [cur, overlays.data, id, n.locked, n.speed, n.batSpeed, n.impactSpeed, frame.fps]);

  const [assignRole, setAssignRole] = useState<"batsman" | "bowler" | null>(null);
  const [assignScope, setAssignScope] = useState<"group" | "clip">("clip");
  const [assignHandle, setAssignHandle] = useState("");
  const [assignEmail, setAssignEmail] = useState("");
  const [assignInviteOpen, setAssignInviteOpen] = useState(false);
  const [assignSearch, setAssignSearch] = useState<UserSearchResult | null>(null);
  const [assignBusy, setAssignBusy] = useState(false);
  const managedTeams = (me?.teams ?? []).filter((t) => t.role === "manager");
  const [assignTeamId, setAssignTeamId] = useState<string>("");
  const [assignNewTeamName, setAssignNewTeamName] = useState("");

  const matchTeamId = list.data?.team_id ?? roster.assignment?.team_id ?? video.data?.team?.id ?? null;
  const canAssignClips = !betaReview && !!(video.data?.can_assign || roster.assignment?.players_assignable || managedTeams.length > 0 || video.data?.can_change_team);

  const openAssignSheet = (r: "batsman" | "bowler") => {
    if (!cur) return;
    const pid = r === "batsman" ? roster.shown(cur.batsman_id) : roster.shown(cur.bowler_id);
    const existing = r === "batsman" ? cur.assignees?.batsman.assignee : cur.assignees?.bowler.assignee;
    setAssignRole(r);
    setAssignScope(pid ? "group" : "clip");
    setAssignHandle(existing?.handle ?? "");
    setAssignEmail("");
    setAssignInviteOpen(false);
    setAssignTeamId(matchTeamId || managedTeams[0]?.id || "");
    setAssignNewTeamName("");
  };

  const assignSlug = cleanHandleInput(assignHandle);
  useEffect(() => {
    if (!assignRole || !matchTeamId) {
      setAssignSearch(null);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(() => {
      get<UserSearchResult>(`/v1/users/search?q=${encodeURIComponent(assignSlug)}&team_id=${encodeURIComponent(matchTeamId)}`)
        .then((res) => {
          if (!cancelled) setAssignSearch(res);
        })
        .catch(() => {});
    }, 140);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [assignRole, matchTeamId, assignSlug]);

  const applyClipAssignment = async (target: Record<string, unknown>, msg: string) => {
    if (!cur || !assignRole || assignBusy) return;
    setAssignBusy(true);
    try {
      const pid = assignRole === "batsman" ? roster.shown(cur.batsman_id) : roster.shown(cur.bowler_id);
      const teamExtra = !matchTeamId
        ? assignTeamId
          ? { team_id: assignTeamId }
          : assignNewTeamName.trim()
            ? { new_team_name: assignNewTeamName.trim() }
            : {}
        : {};
      if (assignScope === "group" && pid) {
        const updatedRoster = await put<Roster>(`/v1/videos/${id}/players/${pid}/assignment`, { ...target, ...teamExtra });
        rosterApi.set(updatedRoster);
        await Promise.all([list.reload(), video.reload(), refresh()]);
      } else {
        const res = await put<{ delivery_id: string; assignees: ClipAssignees }>(`/v1/deliveries/${cur.id}/assignment`, {
          role: assignRole,
          ...target,
          ...teamExtra,
        });
        patch(cur.id, (c) => ({ ...c, assignees: res.assignees }));
        if (!matchTeamId) await Promise.all([rosterApi.reload(), list.reload(), video.reload(), refresh()]);
      }
      setAssignRole(null);
      say(msg);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404 && e.details?.reason === "UNKNOWN_HANDLE") {
        setAssignInviteOpen(true);
      } else {
        say((e as Error).message);
      }
    } finally {
      setAssignBusy(false);
    }
  };

  const assignBar = cur && canAssignClips && (
    <div className="row mt10" style={{ justifyContent: "flex-start", gap: 8, flexWrap: "wrap" }} data-testid="clip-assign-bar">
      {(["batsman", "bowler"] as const).map((r) => {
        const roleInfo = r === "batsman" ? cur.assignees?.batsman : cur.assignees?.bowler;
        const pid = r === "batsman" ? roster.shown(cur.batsman_id) : roster.shown(cur.bowler_id);
        const asg = roleInfo?.assignee ?? null;
        const isUnassigned = !!roleInfo?.unassigned;
        const val = isUnassigned
          ? "Unassigned"
          : asg?.handle
            ? `@${asg.handle}`
            : pid
              ? roster.name(pid)
              : "Assign @handle";
        const hasHandle = !isUnassigned && !!asg?.handle;
        return (
          <button
            key={r}
            type="button"
            className={`atag ${hasHandle ? "assigned" : isUnassigned || !pid ? "empty" : ""}`}
            onClick={() => openAssignSheet(r)}
            data-testid={`clip-assign-${r}`}
          >
            <span className="k">{r === "batsman" ? "Batsman" : "Bowler"}</span>
            <span className="v">{val}</span>
            {roleInfo?.source === "clip" && <span className="tiny">(this ball)</span>}
          </button>
        );
      })}
    </div>
  );

  const notADelivery = !betaReview && cur && (
    cur.flagged
      ? <button className="u tiny" onClick={() => flag(cur, false)} data-testid="unflag">Not a delivery · undo</button>
      : <button className="u tiny" onClick={() => setConfirmFlag(cur)} data-testid="flag">Not a delivery</button>
  );
  const factsLine = cur && (
    n.locked ? (
      <button className="btn ghost sm" onClick={() => unlock(cur)} disabled={previewsLeft <= 0} data-testid="show-numbers">
        {previewsLeft > 0 ? `Show numbers · ${previewsLeft} of ${list.data?.preview?.allowed ?? 5} left` : "Numbers on Basic"}
      </button>
    ) : (
      <>
        {n.speed != null ? (
          <>
            <span data-testid="speed">{n.speed}</span> km/h
            {hitLine(n) && <span data-testid="hit-speeds"> · {hitLine(n)}</span>}
          </>
        ) : (
          <span>Speed unavailable{n.speedNote ? ` · ${n.speedNote}` : ""}</span>
        )}
        {placeLine(n) ? ` · ${placeLine(n)}` : ""}
      </>
    )
  );
  const dlThis = !betaReview && <button className="btn dark" onClick={() => cur && (exp.state.phase === "ready" ? window.open(exp.state.url, "_blank") : exp.start([cur.id], "mp4", downloadName(roster, { batsman, bowler }, i + 1)))} disabled={!cur || playback?.gap || cur.flagged || exp.state.phase === "requesting" || exp.state.phase === "preparing"} data-testid="download-this">{exportLabel(exp.state, "Download this")}</button>;
  const dlAll = !betaReview && <button className="btn ghost" onClick={() => expAll.state.phase === "ready" ? window.open(expAll.state.url, "_blank") : expAll.start(clips.filter((c) => !c.flagged).map((c) => c.id), "mp4", downloadName(roster, { batsman, bowler }, "all"))} disabled={clips.length === 0 || showFlagged || expAll.state.phase === "requesting" || expAll.state.phase === "preparing"} data-testid="download-all">{exportLabel(expAll.state, `All ${clips.length}`)}</button>;


  return (
    <Shell back={matchPath} right={<button className="r" onClick={() => say("Sharing arrives with public moments.")}>Share</button>} foot={!desktop && !betaReview && <div className="grid2">{dlThis}{dlAll}</div>}>
      <div className="p lv-pp timeline-page" style={{ paddingTop: desktop ? 24 : 4, paddingBottom: 24 }} data-testid="clips-page">
        {betaReview && <p className="tiny" style={{ marginBottom: 16 }}><Link className="u" to={matchPath} data-testid="review-back">Back to submission</Link></p>}
        <header className="row between lv-pp-head" data-testid="live-player-head">
          <div className="row lv-phead">
            <div>
              <div className="tiny">{video.data?.title}{label ? ` · ${label}` : ""}</div>
              <h2 data-testid="live-player-name">{who}</h2>
              {betaReview && video.data?.uploader_email && <p className="tiny mt6" style={{ overflowWrap: "anywhere" }} data-testid="submission-email">{video.data.uploader_email}</p>}
              <div className="sub">{role ? `${role === "batsman" ? "Batsman" : "Bowler"} · ${plural(clips.length, "clip")}` : plural(clips.length, "clip")}</div>
            </div>
          </div>
          {desktop && !betaReview && <div className="row">{dlThis}{dlAll}</div>}
        </header>
        <ErrorLine error={list.error} />
        {cur ? (
          <div className="lv-pp-top mt24">
            <div className="lv-pp-main" data-testid="live-stage">
              <div ref={stage} className={`timeline-stage ${full ? "full" : ""}`}>
              <LiveClipPlayer
                src={src}
                overlay={playback?.gap ? null : overlay}
                frameSize={frame}
                fps={frame.fps}
                showSpeeds={render.speeds}
                showTraces={render.traces}
                autoPlay
                onEnded={nextClip}
                label={`clip ${i + 1}`}
                description={playback?.gap ? "Match video between delivery clips. No delivery measurements are selected." : deliverySummary(`Clip ${i + 1} of ${clips.length}`, n, bowlerOf(cur), batsmanOf(cur))}
                videoRef={videoEl}
                seek={playback?.seek ?? null}
                timeOffsetMs={mediaOffset}
              >
                <MatchPlaybackControls video={videoEl} src={src} time={time} duration={source ? duration : mediaOffset + (Number.isFinite(videoEl.current?.duration) ? videoEl.current!.duration * 1000 : 0)} start={mediaOffset} offset={offset} onStart={startInspection} onCancel={cancelInspection} onSeek={(ms) => seekMatch(ms, null)}
                  onPrevious={() => selectClip(i - 1)} onNext={() => selectClip(i + 1)} previousDisabled={i <= 0} nextDisabled={i >= clips.length - 1}>
                <button className="timeline-fullscreen" aria-label={full ? "Exit full screen" : "Full screen"} data-testid="fullscreen" onClick={() => full ? exitFull() : stage.current && enterFull(stage.current, videoEl.current)}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d={full ? "M9 3v6H3m18 0h-6V3M3 15h6v6m6 0v-6h6" : "M9 3H3v6m12-6h6v6M3 15v6h6m6 0h6v-6"} /></svg></button>
                <ViewingOptions speeds={render.speeds} traces={render.traces} auto={auto} onRender={setRender} onAuto={toggleAuto} />
                </MatchPlaybackControls>
              </LiveClipPlayer>
              </div>
              <div className="med lv-pp-title" data-testid="clip-title">
                {playback?.gap ? `Match · ${fmtClipClock(offset + time)}` : `Clip ${i + 1} of ${clips.length} · ${fmtClipClock(offset + clipAnchor(cur))}`} · {playback?.gap ? "Between clips" : `${bowlerOf(cur)} to ${batsmanOf(cur)}`}
              </div>
              {(!src || mediaState !== "ready") && <div className="timeline-source-status" role="status">
                {!src ? "Full match video is unavailable." : mediaState === "error" ? "Video could not load." : "Loading video…"}
                {src && mediaState === "error" && <button className="u" onClick={() => {
                  const v = videoEl.current; if (!v) return;
                  const ms = time; setMediaState("loading");
                  setPlayback({ src, gap: playback?.gap ?? false, seek: { seconds: (ms - mediaOffset) / 1000, playing: resumePlayback.current, serial: ++serial.current } });
                  v.load();
                }}>Retry video</button>}
              </div>}
              {!source && src && <p className="tiny mt8">Full match seeking is unavailable. Use the clip arrows.</p>}
              <div className="lv-toolbar">
                <div className="tiny" data-testid="clip-facts">{playback?.gap ? "Choose a clip to see its numbers." : <>{factsLine} · {notADelivery}</>}</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="notice mt24">No clips here{showFlagged ? "" : " yet"}.</div>
        )}
        {cur && <MatchTimeline clips={clips} index={i} duration={duration} time={time} offset={offset} seekable={!!source} atGap={playback?.gap ?? false} onStart={startInspection} onCancel={cancelInspection} onSeek={seekMatch} onStep={selectClip}
          name={(c, r) => r === "batsman" ? batsmanOf(c) : bowlerOf(c)} />}
        {assignBar && !playback?.gap && <details className="timeline-manager"><summary>Assign players</summary>{assignBar}</details>}
        {(batsman || bowler) && <p className="tiny mt16"><button className="u" onClick={() => nav(matchPath)}>All players</button> · <button className="u" onClick={() => nav(`${matchPath}/clips`)}>Whole match</button></p>}
        {plan === "free" && list.data?.preview && <p className="tiny mt10">Free shows numbers on {list.data.preview.allowed} clips you pick; {list.data.preview.used} used on this video.</p>}
      </div>
      {confirmFlag && (
        <Sheet onClose={() => setConfirmFlag(null)}>
          <div data-testid="flag-sheet">
            <h2>{confirmFlag.flagged ? "Restore this clip?" : "Not a delivery?"}</h2>
            <p className="sub mt10">{confirmFlag.flagged ? "It goes back into your clips." : "It leaves your clips and downloads. We use the flag to get better; nothing else changes."}</p>
            <div className="mt24"><button className="btn dark full" onClick={() => flag(confirmFlag, !confirmFlag.flagged)} data-testid="flag-confirm">{confirmFlag.flagged ? "Restore" : "Mark as not a delivery"}</button></div>
            <div className="mt10"><button className="btn ghost full" onClick={() => setConfirmFlag(null)}>Keep it</button></div>
          </div>
        </Sheet>
      )}
      {assignRole && cur && (
        <Sheet onClose={() => !assignBusy && setAssignRole(null)}>
          <div data-testid="clip-assign-sheet">
            <h2>Assign {assignRole}</h2>
            {(() => {
              const pid = assignRole === "batsman" ? roster.shown(cur.batsman_id) : roster.shown(cur.bowler_id);
              const groupPlayer = pid
                ? [...roster.batsmen, ...roster.bowlers].find((p) => p.id === pid) ?? null
                : null;
              return (
                <>
                  <div className="stack mt16" style={{ gap: 8 }}>
                    {groupPlayer && (
                      <div
                        className={`scope-opt ${assignScope === "group" ? "on" : ""}`}
                        onClick={() => setAssignScope("group")}
                        data-testid="scope-group"
                      >
                        <span className="scope-radio"><i /></span>
                        <div>
                          <div className="med">All {groupPlayer.clip_count} clips of {roster.label(groupPlayer.id)}</div>
                          <div className="tiny">Assigns the whole player group across this match</div>
                        </div>
                      </div>
                    )}
                    <div
                      className={`scope-opt ${assignScope === "clip" ? "on" : ""}`}
                      onClick={() => setAssignScope("clip")}
                      data-testid="scope-clip"
                    >
                      <span className="scope-radio"><i /></span>
                      <div>
                        <div className="med">This ball only (#{cur.index})</div>
                        <div className="tiny">Overrides the player group for just this delivery</div>
                      </div>
                    </div>
                  </div>

                  {!matchTeamId && (
                    <div className="notice mt16">
                      <div className="tiny">Link match to a team</div>
                      {managedTeams.length > 0 ? (
                        <select
                          className="field mt6"
                          value={assignTeamId}
                          onChange={(e) => setAssignTeamId(e.target.value)}
                          data-testid="clip-assign-team-select"
                        >
                          {managedTeams.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className="field mt6"
                          value={assignNewTeamName}
                          onChange={(e) => setAssignNewTeamName(e.target.value)}
                          placeholder="Team name (e.g. Vikings XI)"
                          data-testid="clip-assign-new-team"
                        />
                      )}
                    </div>
                  )}

                  <label className="tiny mt16" style={{ display: "block" }}>Player @handle</label>
                  <div className="handle-field mt6">
                    <span className="handle-at">@</span>
                    <input
                      className="field handle-in"
                      value={assignHandle}
                      onChange={(e) => {
                        setAssignHandle(e.target.value.replace(/^@+/, "").toLowerCase());
                        setAssignInviteOpen(false);
                      }}
                      placeholder="rohan"
                      data-testid="clip-assign-handle-input"
                    />
                  </div>

                  {assignSearch && (assignSearch.team_members.length > 0 || assignSearch.other_users.length > 0) && (
                    <div className="mt8" style={{ border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden" }}>
                      {assignSearch.team_members.map((m) => (
                        <button
                          key={m.user_id}
                          type="button"
                          className="combo-row"
                          onClick={() =>
                            m.handle &&
                            applyClipAssignment({ user_id: m.user_id }, `Assigned to @${m.handle}`)
                          }
                          data-testid={`clip-combo-member-${m.handle}`}
                        >
                          <span className="combo-av">{initialsOf(m.display_name, m.handle)}</span>
                          <div>
                            <div className="h">@{m.handle}</div>
                            <div className="n">{m.display_name}</div>
                          </div>
                          <span className="r">Team</span>
                        </button>
                      ))}
                      {assignSearch.other_users.map((u) => (
                        <button
                          key={u.user_id}
                          type="button"
                          className="combo-row"
                          onClick={() =>
                            u.handle &&
                            applyClipAssignment({ user_id: u.user_id }, `Assigned to @${u.handle}`)
                          }
                          data-testid={`clip-combo-user-${u.handle}`}
                        >
                          <span className="combo-av">{initialsOf(u.display_name, u.handle)}</span>
                          <div>
                            <div className="h">@{u.handle}</div>
                            <div className="n">{u.display_name}</div>
                          </div>
                          <span className="r">+ Add</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {assignInviteOpen && (
                    <div className="mt12">
                      <label className="tiny" style={{ display: "block" }}>Invite @{assignSlug} by email</label>
                      <input
                        type="email"
                        className="field mt6"
                        value={assignEmail}
                        onChange={(e) => setAssignEmail(e.target.value)}
                        placeholder={`${assignSlug}@gmail.com`}
                        data-testid="clip-assign-email-input"
                      />
                    </div>
                  )}

                  <div className="mt20 stack" style={{ gap: 10 }}>
                    <button
                      type="button"
                      className="btn dark full"
                      disabled={assignBusy || !assignSlug || (assignInviteOpen && !assignEmail.trim())}
                      onClick={() => {
                        if (assignInviteOpen) {
                          void applyClipAssignment(
                            { invite: { handle: assignSlug, email: assignEmail.trim() } },
                            `Invited @${assignSlug} & assigned`,
                          );
                        } else {
                          void applyClipAssignment({ handle: assignSlug }, `Assigned to @${assignSlug}`);
                        }
                      }}
                      data-testid="clip-assign-save"
                    >
                      {assignBusy ? "Saving…" : assignInviteOpen ? `Invite & assign @${assignSlug}` : `Assign @${assignSlug || "handle"}`}
                    </button>
                    <button
                      type="button"
                      className="btn ghost full"
                      disabled={assignBusy}
                      onClick={() => applyClipAssignment({ unassigned: true }, `Removed ${assignRole} tag from ball #${cur.index}.`)}
                      data-testid="clip-unassign-btn"
                    >
                      Remove {assignRole} tag from this ball
                    </button>
                    <button type="button" className="btn ghost full" onClick={() => setAssignRole(null)} disabled={assignBusy}>
                      Cancel
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </Sheet>
      )}
      {toast}
    </Shell>
  );
}

const AUTOPLAY_KEY = "dt.autoplay";

/** Autoplay is on unless this browser turned it off; storage can be missing (a private window) or throw. */
function readAutoplay(): boolean {
  try {
    return localStorage.getItem(AUTOPLAY_KEY) !== "0";
  } catch {
    return true;
  }
}

function writeAutoplay(on: boolean): void {
  try {
    localStorage.setItem(AUTOPLAY_KEY, on ? "1" : "0");
  } catch {
    // the switch still works for this page
  }
}

function fmtClipClock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`
    : `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

export function clipsCount(list: ClipList | null): string {
  return list ? plural(list.summary.clips, "clip") : "";
}
